/* ═══════════ LES 5 SITES DE L'EMPIRE — configuration des contenus ═══════════
   Chaque domaine reçoit son propre héros, sa narration, ses vedettes et
   l'ordre de parade des 13 marques « rangées le long du site » — à l'image
   du site de référence où chaque collection occupe un salon successif. */

import { MARQUES, type Vedette } from "./catalog";

export type SiteSlug =
  | "anta-khalifa-com"
  | "adji-anta-diop"
  | "bouye-de-ndiamsyl"
  | "anta-khalifa-fr"
  | "anta-khalifa-online";

export type Bloc =
  | "countdown"
  | "vedettes"
  | "mawlid"
  | "vitrine"
  | "video"
  | "portails"
  | "packs";

export interface SiteConfig {
  slug: SiteSlug;
  domaine: string;
  onglet: string; // libellé court des onglets du changeur de domaines
  nom: string;
  particule: string;
  slogan: string;
  narration: string;
  role: string;
  heroVisuel: string;
  heroAlt: string;
  theme: "or" | "mawlid" | "vert" | "bordeaux" | "onyx";
  ordreMarques: string[]; // slugs rangés le long du site
  modeGammesBouye?: boolean; // BOUYE déclinée en 4 gammes-salons
  filtres?: boolean; // barre recherche / formats / tri (grande boutique)
  avant: Bloc[]; // blocs avant la parade des marques
  apres: Bloc[]; // blocs après la parade
  vedettes: Vedette[];
}

/* ─────────────── Aide : retire une vedette directement du catalogue ─────────────── */
function ved(slug: string, num: number, accroche: string): Vedette {
  const m = MARQUES.find((x) => x.slug === slug);
  const p = m?.produits.find((x) => x.num === num);
  if (!m || !p) throw new Error("Vedette introuvable : " + slug + " #" + num);
  return { file: p.file, nom: p.name, marque: m.nom, marqueSlug: m.slug, num, accroche };
}

/* ─────────────── Ordre impérial standard des 14 échoppes ─────────────── */
export const ORDRE_IMPERIAL = [
  "ndiamsyl-mody", "bouye-ndiamsyl", "guinaru-antayi", "sunu-naggi-mame",
  "vigne-de-beziers", "dom-perpignan", "cristal-noble", "source-aurea",
  "l-eau-doree", "riviere-de-luxe", "purete-royale", "aqua-magnifica",
  "maoun", "excellence-mouride-baye-fall",
];

/* ─────────────── Textes éditoriaux des salons (sectionheaders) ─────────────── */
export const EDITO: Record<string, { titre: string; texte: string }> = {
  "ndiamsyl-mody": {
    titre: "La Maison Impériale NDIAMSYL MODY",
    texte: "La Maison-mère couronne l'Empire : pourpre impérial et or 24K, eaux d'apparat et éditions numérotées « Premium Water Excellence ». De l'Amphore Héritage Mosquée aux canettes Onyx, chaque flacon porte le sceau du trône et veille sur les maisons qu'elle protège.",
  },
  "bouye-ndiamsyl": {
    titre: "BOUYE DE NDIAMSYL — les rideaux verts & or",
    texte: "Sous les rideaux de velours vert émeraude brodés d'or vit la boisson des grandes nuits : le bouye de baobab, doux et généreux. 99 créations — canettes bijoux 33 cl, formats famille, eaux de prestige et coffrets de gala — célèbrent Mawlid Naby et le Gamou sur chaque étiquette couronnée.",
  },
  "guinaru-antayi": {
    titre: "GUINARU ANTAYI — la place du bordeaux",
    texte: "Le rouge bordeaux et l'or : voilà la maison de la volaille d'honneur. Poulets entiers, yassa, dibi, braisés et coffrets de fête — 33 créations qui font briller les tablées du Gamou et les grandes réunions de famille.",
  },
  "sunu-naggi-mame": {
    titre: "SUNU NAGGI MAME — la bonté du bovin & du lait",
    texte: "Vert émeraude et or pour la maison des troupeaux : entrecôtes persillées, dibi royal, kilishi, laits entiers, soungrou, yaourts et fromages wagashi — 66 créations qui honorent la noblesse de Mame et la générosité des vergers laitiers.",
  },
  "vigne-de-beziers": {
    titre: "VIGNE DE BEZIERS — améthyste & or",
    texte: "La plus méridionale des eaux du Prince : treize éditions où la vigne de Béziers prête ses reflets violets à des flacons d'exception, de la canette de gala au magnum couronné.",
  },
  "dom-perpignan": {
    titre: "DOM PERPIGNAN — bleu nuit & or",
    texte: "L'éclat des cours d'apparat : treize eaux de prestige qui portent le nom de Perpignan comme une écharpe de draps d'or, entre éditions Roussillon et jéroboams de cérémonie.",
  },
  "cristal-noble": {
    titre: "CRISTAL NOBLE — blanc givré, argent & or",
    texte: "La pureté sculptée comme un joyau : cristaux gravés, éditions Diamant et Soleil Couchant — treize eaux taillées au tranchant de la lumière.",
  },
  "source-aurea": {
    titre: "SOURCE AUREA — ambre miel & or",
    texte: "La source cachée du palais : douze flacons ambrés, de la fontaine de table au jéroboam scellé, pour ceux qui cherchent la douceur d'une eau née au cœur de l'or.",
  },
  "l-eau-doree": {
    titre: "L'EAU DORÉE — or champagne & ivoire",
    texte: "L'éclat des réceptions : douze éditions où l'eau semble versée directement du soleil, entre carafes ivoire et fontaines de banquet.",
  },
  "riviere-de-luxe": {
    titre: "RIVIÈRE DE LUXE — émeraude-teal & or",
    texte: "Le fleuve des fastes : douze eaux de rivière magnifiées, de la Perle du Fleuve aux formats impériaux, pour les tablées qui ne s'arrêtent qu'à l'aube.",
  },
  "purete-royale": {
    titre: "PURETÉ ROYALE — blanc perle & or",
    texte: "La sobriété couronnée : douze eaux d'une pureté royale, délicates comme une perle posée sur du velours — le luxe qui ne crie jamais.",
  },
  "aqua-magnifica": {
    titre: "AQUA MAGNIFICA — saphir & or",
    texte: "La magnificence bleue : douze eaux étoilées, du Saphir Étoilé aux coffrets d'apparat, qui éteignent la soif comme on éteint une ville — en lumière.",
  },
  "maoun": {
    titre: "MAOUN — noir onyx & or, la maison des sens",
    texte: "Eaux royales, sodas pétillants et parfums de prestige : Oud Impérial de La Mecque, Santal Royal de Médine, Ambre Éternel — 33 créations pour habiller le corps et l'âme.",
  },
  "excellence-mouride-baye-fall": {
    titre: "EXCELLENCE MOURIDE-BAYE FALL — vert Touba & or",
    texte: "L'excellence spirituelle : encens et khourel, parfums et huiles précieuses, soins et coffrets d'exception — 33 créations dédiées à la mémoire et à la louange.",
  },
};

/* ─────────────── Les 4 gammes-salons de BOUYE (site dédié) ─────────────── */
export const GAMMES_BOUYE = [
  { id: "bouye-canettes", titre: "Canettes 33 cl — le bijou vert & or", filtre: (n: number, nom: string) => n <= 33 && !/coffret/i.test(nom), sous: "Les premières créations : canettes couronnées, éditions Rubis, Raisin de Médine et saveurs de fête, l'éclat du bijou dans chaque main." },
  { id: "bouye-pet", titre: "PET & formats famille", filtre: (n: number) => n >= 34 && n <= 66, sous: "Les méga-formats généreux — PET premium, bouteilles 1 L et 2 L, packs de gala — pensés pour les grandes tablées du Gamou." },
  { id: "bouye-verre", titre: "Verre, eaux & prestige", filtre: (n: number, nom: string) => n >= 67 && !/coffret/i.test(nom), sous: "Flacons de verre, eaux fines et éditions numérotées : le versant le plus solennel de la maison verte." },
  { id: "bouye-coffrets", titre: "Coffrets cadeaux de gala", filtre: (n: number, nom: string) => /coffret/i.test(nom), sous: "Les écrins velours vert & or, rubans bordeaux et sceaux dorés — le cadeau des nuits bénies, mis en avant au sommet du site." },
];

/* ─────────────── Les 5 sites ─────────────── */
export const SITES: SiteConfig[] = [
  /* ════ 1. www.Anta-Khalifa.com — Le Trône ════ */
  {
    slug: "anta-khalifa-com",
    domaine: "www.Anta-Khalifa.com",
    onglet: "Anta-Khalifa.com",
    nom: "ANTAKHALIFA",
    particule: "Le site du trône",
    slogan: "Le Trône des 13 Maisons",
    narration:
      "Ici règne la Maison impériale NDIAMSYL MODY : les 13 marques défilent le long du site comme les salles d'un palais — du pourpre et de l'or 24K au vert émeraude des rideaux Bouye, jusqu'au rouge bordeaux des fêtes de Guinaru Antayi. Chaque salon expose ses créations couronnées, toutes signées du même serment.",
    role: "Le trône principal de l'Empire Anta Khalifa",
    heroVisuel: "/produits/empire-vitrine-finale/101_vitrine-finale-empire-13-marques.png",
    heroAlt: "La vitrine finale des 13 marques en fer à cheval sous le dais couronné",
    theme: "or",
    ordreMarques: ORDRE_IMPERIAL,
    avant: ["countdown", "vedettes", "vitrine"],
    apres: ["video", "portails", "packs"],
    vedettes: [
      ved("ndiamsyl-mody", 1, "L'eau impériale de la Maison-mère"),
      ved("bouye-ndiamsyl", 99, "Les 99 créations en un seul écrin"),
      ved("guinaru-antayi", 1, "La volaille des fêtes familiales"),
      ved("sunu-naggi-mame", 1, "La noblesse du bovin sénégalais"),
      ved("cristal-noble", 27, "La pureté taillée en diamant"),
      ved("l-eau-doree", 52, "L'or liquide des réceptions"),
      ved("maoun", 30, "Deux trésors noirs & or"),
      ved("excellence-mouride-baye-fall", 33, "Les parfums de Touba en majesté"),
    ],
  },

  /* ════ 2. AdjiAntaDiop.ligue-islamique.biz — Le Portail Impérial ════ */
  {
    slug: "adji-anta-diop",
    domaine: "AdjiAntaDiop.ligue-islamique.biz",
    onglet: "AdjiAntaDiop.biz",
    nom: "ADJI ANTA DIOP",
    particule: "Le portail impérial",
    slogan: "Le Premier et le Meilleur",
    narration:
      "Le portail-mémoire de l'Empire : ici, Mawlid Naby et le Gamou ouvrent la marche, les coffrets cadeaux Bouye de Ndiamsyl précèdent les 13 maisons, et la signature complète veille sur chaque étiquette comme un vœu pieux devenu promesse tenue. La vidéo impériale et les huit portails amis prolongent la célébration.",
    role: "Le portail impérial — mémoire et héritage d'Adji Anta Diop",
    heroVisuel: "/visuels/01_hero-mawlid-naby-gamou.png",
    heroAlt: "Célébration Mawlid Naby et Gamou — coffrets Bouye de Ndiamsyl devant les rideaux verts et or",
    theme: "mawlid",
    ordreMarques: ORDRE_IMPERIAL,
    avant: ["countdown", "mawlid", "vedettes"],
    apres: ["video", "portails"],
    vedettes: [
      ved("bouye-ndiamsyl", 97, "L'éclat des veillées de Mawlid"),
      ved("bouye-ndiamsyl", 99, "L'écrin complet des 99 créations"),
      ved("bouye-ndiamsyl", 88, "Le coffret des grandes nuits"),
      ved("bouye-ndiamsyl", 33, "Le trio des trois couleurs du trône"),
      ved("ndiamsyl-mody", 2, "Le prestige de la couronne pourpre"),
      ved("guinaru-antayi", 33, "Le coffret bordeaux des fêtes"),
      ved("maoun", 30, "Deux parfums noirs & or à offrir"),
      ved("excellence-mouride-baye-fall", 33, "L'excellence de Touba en coffret"),
    ],
  },

  /* ════ 3. www.Bouye-de-Ndiamsyl.online — La Maison Verte ════ */
  {
    slug: "bouye-de-ndiamsyl",
    domaine: "www.Bouye-de-Ndiamsyl.online",
    onglet: "Bouye-de-Ndiamsyl.online",
    nom: "BOUYE DE NDIAMSYL",
    particule: "La maison verte",
    slogan: "La Maison du Bouye de Prestige",
    narration:
      "Les rideaux de velours vert émeraude brodés d'or s'ouvrent sur 99 créations rangées en quatre salons : canettes bijoux, formats famille, eaux de prestige et coffrets de gala. Puis les 13 maisons de l'Empire défilent à leur tour le long du site — sous la même étoile verte et or.",
    role: "La boutique dédiée au bouye — le cœur vert de l'Empire",
    heroVisuel: "/visuels/04_banniere-mawlid-gamou.png",
    heroAlt: "Cinq coffrets de gala Bouye de Ndiamsyl devant les rideaux de velours brodés d'or",
    theme: "vert",
    ordreMarques: ORDRE_IMPERIAL,
    modeGammesBouye: true,
    avant: ["vedettes", "mawlid"],
    apres: ["packs"],
    vedettes: [
      ved("bouye-ndiamsyl", 1, "Le goût fondateur de l'Empire"),
      ved("bouye-ndiamsyl", 10, "La douceur bénie de la Ville Lumière"),
      ved("bouye-ndiamsyl", 31, "Les trois couleurs du trône"),
      ved("bouye-ndiamsyl", 89, "Le format des grandes tablées"),
      ved("bouye-ndiamsyl", 97, "L'éclat des veillées de Mawlid"),
      ved("bouye-ndiamsyl", 99, "Les 99 créations en un seul écrin"),
      ved("bouye-ndiamsyl", 88, "Le coffret des grandes nuits"),
      ved("bouye-ndiamsyl", 33, "Le trio rouge · vert · or"),
    ],
  },

  /* ════ 4. www.Anta-Khalifa.fr — La Galerie Française ════ */
  {
    slug: "anta-khalifa-fr",
    domaine: "www.Anta-Khalifa.fr",
    onglet: "Anta-Khalifa.fr",
    nom: "ANTAKHALIFA FRANCE",
    particule: "La galerie française",
    slogan: "L'art de recevoir — le bordeaux à l'honneur",
    narration:
      "Dans la galerie française, le rouge bordeaux tient la place d'honneur : Guinaru Antayi ouvre la marche, les parfums Maoun embaument les salons, puis les eaux du Prince et la maison verte défilent en salons successifs. Treize maisons le long du site, de l'entrée d'apparat au salon d'or.",
    role: "Anta Khalifa — France : la galerie de l'art de recevoir",
    heroVisuel: "/visuels/03_gamou-celebration.png",
    heroAlt: "Table cérémonielle bordeaux et or — coffrets alignés, lanternes et croissants dorés",
    theme: "bordeaux",
    ordreMarques: [
      "guinaru-antayi", "maoun", "bouye-ndiamsyl", "ndiamsyl-mody",
      "sunu-naggi-mame", "vigne-de-beziers", "dom-perpignan", "cristal-noble",
      "source-aurea", "l-eau-doree", "riviere-de-luxe", "purete-royale",
      "aqua-magnifica", "excellence-mouride-baye-fall",
    ],
    avant: ["vedettes"],
    apres: ["portails", "packs"],
    vedettes: [
      ved("guinaru-antayi", 1, "La volaille des fêtes, en majesté bordeaux"),
      ved("guinaru-antayi", 33, "Le coffret bordeaux de gala"),
      ved("maoun", 33, "Le trio parfumé noir & or"),
      ved("maoun", 30, "Un duo d'ambre pour les salons"),
      ved("bouye-ndiamsyl", 97, "Le coffret doré des veillées"),
      ved("cristal-noble", 27, "Le cristal gravé des tables françaises"),
      ved("source-aurea", 40, "L'ambre miel du palais"),
      ved("l-eau-doree", 52, "L'eau dorée des réceptions"),
    ],
  },

  /* ════ 5. www.Anta-khalifa.online — La Grande Boutique ════ */
  {
    slug: "anta-khalifa-online",
    domaine: "www.Anta-khalifa.online",
    onglet: "Anta-khalifa.online",
    nom: "ANTAKHALIFA EN LIGNE",
    particule: "La grande boutique",
    slogan: "405 créations, 13 maisons, une seule promesse",
    narration:
      "La façade e-commerce de l'Empire : recherchez, filtrez, triez — 405 créations prêtes à partir sous colis scellés à la cire d'or. Les 13 maisons y sont rangées le long du site comme sur les grandes étagères dorées de la vitrine, de la Maison impériale jusqu'à l'excellence de Touba.",
    role: "Anta Khalifa — la grande boutique en ligne de l'Empire",
    heroVisuel: "/produits/empire-vitrine-finale/106_banniere-ecommerce-empire-13-marques.png",
    heroAlt: "Bannière e-commerce — le lineup officiel des 13 marques sur l'étagère miroir dorée",
    theme: "onyx",
    ordreMarques: ORDRE_IMPERIAL,
    filtres: true,
    avant: ["vedettes"],
    apres: ["packs", "video", "portails"],
    vedettes: [
      ved("ndiamsyl-mody", 2, "Le prestige de la couronne"),
      ved("bouye-ndiamsyl", 97, "L'éclat des veillées de Mawlid"),
      ved("guinaru-antayi", 1, "La volaille des fêtes familiales"),
      ved("sunu-naggi-mame", 33, "Les trésors laitiers de Mame"),
      ved("dom-perpignan", 14, "Le bleu nuit des cours d'apparat"),
      ved("aqua-magnifica", 88, "Le saphir étoilé des grandes soifs"),
      ved("maoun", 30, "Deux trésors noirs & or"),
      ved("excellence-mouride-baye-fall", 33, "Les parfums de Touba en majesté"),
    ],
  },
];

/* ─────────────── Compte à rebours royal (Mawlid Naby & Gamou 2027) ─────────────── */
export const COUNTDOWN_CIBLE = "2027-08-15T18:00:00Z";

export function siteParSlug(slug: string): SiteConfig {
  const s = SITES.find((x) => x.slug === slug);
  if (!s) throw new Error("Site inconnu : " + slug);
  return s;
}
